import styled from 'styled-components';

/* ─── Drawer body ──────────────────────────────────── */

export const DrawerHeading = styled.p`
  font-size: var(--theme-type-scale-heading-xs-font-size, 18px);
  font-family: 'NOTHS Suisse Neue', 'Lato', Arial, sans-serif;
  font-weight: 500;
  line-height: var(--theme-type-scale-heading-xs-line-height, 24px);
  letter-spacing: -0.72px;
  color: var(--theme-color-content-primary, #1e1e29);
  margin: 0;
`;

export const DrawerSubDescription = styled.p`
  font-size: var(--font-size\/body-md, 15px);
  font-family: Lato, sans-serif;
  font-weight: 400;
  line-height: var(--line-height\/body-md, 20px);
  letter-spacing: 0.15px;
  color: var(--theme-color-content-secondary, #58585f);
  margin: 0;
`;

export const DrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 16px 16px;
  flex: 1;
  overflow-y: auto;
`;

export const DrawerOptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DrawerRequirements = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;

  > p {
    font-size: var(--font-size\/body-md, 15px);
    font-family: Lato, sans-serif;
    font-weight: 400;
    line-height: var(--line-height\/body-md, 20px);
    letter-spacing: 0.15px;
    color: var(--theme-color-content-secondary, #58585f);
    margin: 0;
  }
`;

export const RejectionList = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

// Drawer-side rejection line. Pass `$indented` for per-reason sub-details
// listed under a count header — they share the primary's typography and
// just gain a left indent so the relationship is visually obvious.
export const RejectionLine = styled.p<{ $indented?: boolean }>`
  margin: 0;
  font-size: var(--font-size\/body-md, 15px);
  font-family: Lato, sans-serif;
  line-height: var(--line-height\/body-md, 20px);
  letter-spacing: 0.15px;
  color: #c0392b;
  ${({ $indented }) => $indented && 'padding-left: 24px;'}
`;

/* ─── Photo grid (inside drawer) ──────────────────── */

export const DrawerPhotoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

// The role="list" wrapper around the photo tiles uses display: contents so
// each tile participates as a direct cell of the surrounding Grid alongside
// the dropzone — giving us a single 2-column layout where the dropzone sits
// next to the last image when there's an odd number of photos.
export const TileList = styled.div`
  display: contents;
`;

export const Tile = styled.div<{ $dragOver?: boolean; $dragging?: boolean; $failed?: boolean }>`
  position: relative;
  height: 164px;
  border-radius: 8px;
  overflow: hidden;
  cursor: grab;
  // Stop the browser from interpreting a touch on the tile as a scroll/zoom
  // gesture so the pointer-event reorder owns the touch sequence.
  touch-action: none;
  outline: 3px dashed transparent;
  outline-offset: -3px;
  transition: opacity 120ms ease;

  ${({ $dragging }) => $dragging && 'opacity: 0.4;'}
  ${({ $dragOver }) => $dragOver && 'outline-color: #58585f;'}
  ${({ $failed }) =>
    $failed &&
    `
      outline-style: solid;
      outline-color: #c0392b;
    `}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: opacity 120ms ease;
    -webkit-touch-callout: none;
    user-select: none;
    ${({ $dragOver, $failed }) => ($dragOver || $failed) && 'opacity: 0.4;'}
  }
`;

// Floating ghost preview shown next to the pointer during a tile reorder —
// fills the gap left by the browser's missing HTML5 drag image when we use
// Pointer Events instead. width/height/transform are set inline by the grid
// so the preview matches the source tile and tracks the cursor offset.
export const DragFollower = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  pointer-events: none;
  border-radius: 8px;
  overflow: hidden;
  opacity: 0.85;
  box-shadow: 0 10px 24px rgba(30, 30, 41, 0.25);

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    -webkit-touch-callout: none;
    user-select: none;
  }
`;

export const DeleteButton = styled.button<{ $failed?: boolean }>`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${({ $failed }) => ($failed ? '#fbe7e6' : '#ffffff')};
  box-shadow: 0 0 1px rgba(30, 30, 41, 0.16);
  color: ${({ $failed }) => ($failed ? '#c0392b' : '#1e1e29')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
`;

export const TilePositionBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(30, 30, 41, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  color: #ffffff;
  pointer-events: none;
`;

export const TileDragHandle = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 1px rgba(30, 30, 41, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  pointer-events: none;
`;

// Interactive retry control shown in the centre of a failed tile in place
// of the drag handle. Clears the failed-state for that URL locally so the
// next Confirm re-attempts the upload — the actual upload only ever happens
// on Confirm, never here.
export const TileRefreshButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 1px rgba(30, 30, 41, 0.16);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  cursor: pointer;
  color: #1e1e29;
`;

/* ─── Dropzone (add-more area inside drawer) ──────── */

// When the drawer has no photos, PhotoPickerGrid is not rendered and the
// dropzone is the only child of the surrounding Grid — span both columns
// so it doesn't sit alone in a half-width cell.
export const Dropzone = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 164px;
  border: 1px dashed #bbbbbe;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  padding: 12px;
  gap: 12px;
  text-align: center;
  color: var(--01---Neutrals-Product-Black, #0e0e11);
  font-family: 'NOTHS Sans No3';
  font-size: var(--font-size-Body-MD, 16px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--line-height-Body-MD, 24px);

  &:only-child {
    grid-column: 1 / -1;
  }
`;

/* ─── Empty trigger ────────────────────────────────── */

export const TriggerEmpty = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 159px;
  padding: 12px;
  gap: 12px;
  border: 1px dashed #58585f;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  font-size: var(--font-size\/body-md, 15px);
  font-family: Lato, sans-serif;
  color: #1e1e29;
`;

/* ─── Filled trigger ───────────────────────────────── */

export const TriggerFilled = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  gap: 12px;
  border: 1px solid #138900;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
`;

export const TriggerLabel = styled.span`
  font-size: var(--font-size\/body-sm, 12px);
  font-family: 'NOTHS Sans No3', sans-serif;
  font-weight: 600;
  letter-spacing: -0.12px;
  color: #1e1e29;
`;

export const TriggerFilledGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  img[data-errored='true'] {
    filter: grayscale(1);
    opacity: 0.5;
  }
`;

export const TriggerFilledRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
  height: 160px;

  > *:only-child {
    grid-column: 1 / -1;
  }

  > * {
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    display: block;
    -webkit-touch-callout: none;
    user-select: none;
  }
`;

export const TriggerAddMoreTile = styled.span`
  width: 100%;
  height: 160px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px solid #cbc9c6;
  border-radius: 8px;
  font-size: var(--font-size\/body-md, 15px);
  font-family: Lato, sans-serif;
  color: #0e0e11;
  text-align: center;
  padding: 12px;
`;

export const TriggerMiniGrid = styled.span`
  width: 100%;
  height: 160px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 4px;
`;

export const TriggerMiniCell = styled.span`
  position: relative;
  overflow: hidden;
  border-radius: 4px;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    -webkit-touch-callout: none;
    user-select: none;
  }
`;

export const TriggerOverflowBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 1px rgba(30, 30, 41, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e1e29;
  font-family: 'NOTHS Sans No3', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.14px;
`;

/* ─── Footer ───────────────────────────────────────── */

// Sits directly above the footer buttons when the customer tries to dismiss
// the drawer after destructive edits — prompts them to either confirm or
// cancel using the existing footer actions.
export const UnsavedChangesBanner = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-top: 1px solid #e9e9ea;
  background: #fff8e0;
  color: #1e1e29;
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  flex-shrink: 0;
`;

export const Footer = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 16px 16px;
  border-top: 1px solid #e9e9ea;
  background: #ffffff;
  flex-shrink: 0;
`;

export const FooterButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  height: 44px;
  padding: 12px 20px;
  border-radius: 400px;
  cursor: pointer;
  font-size: 15px;
  font-family: Lato, sans-serif;
  font-weight: 700;
  flex: ${({ $variant }) => ($variant === 'primary' ? '1' : '0 0 auto')};
  border: ${({ $variant }) => ($variant === 'secondary' ? '1px solid #b7b7b8' : 'none')};
  background: ${({ $variant }) => ($variant === 'primary' ? '#3a1945' : '#ffffff')};
  color: ${({ $variant }) => ($variant === 'primary' ? '#ffffff' : '#3a1945')};
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/* ─── Status / error messages ──────────────────────── */

export const StatusMessage = styled.p`
  font-size: 14px;
  color: #666;
  margin: 8px 0 0;
`;

export const MinWarning = styled.p`
  font-size: 12px;
  color: #c0392b;
  margin: 8px 0 0;
`;

// Stacks inline Feedback rows (one per rejection line) under the trigger
// when the customer picks invalid files outside the drawer. Uses flex `gap`
// for spacing so entries align consistently with form-level error messages.
export const InlineFeedbackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

// One row inside `InlineFeedbackList`. Hosts a design-system `Feedback`
// component (which renders two side-by-side <span>s) — display:flex keeps
// them in the natural row order. Pass `$indented` for sub-detail rows so
// they line up under the primary row's text rather than its icon.
export const InlineFeedbackRow = styled.div<{ $indented?: boolean }>`
  display: flex;
  ${({ $indented }) => $indented && 'padding-left: 24px;'}
`;
